import React, { useState } from 'react';
import { ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { useSignal } from 'beatlejs/integrations/react';
import { $Auth } from '@/services/$AuthService';
import { cn } from '@/utils/cn';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

const LogoutIcon = ArrowRightOnRectangleIcon as React.ComponentType<{
  className?: string;
}>;

interface MainLayoutProps {
  children: React.ReactNode;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const isPrivateStaging = import.meta.env.VITE_BAMBO_ENVIRONMENT === 'private_staging';
  const [auth] = useSignal([$Auth]);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);

  const confirmLogout = () => {
    setIsLogoutOpen(false);
    void auth.logout();
  };

  return (
    <div className="min-h-screen bg-[var(--pm-bg)] text-slate-950" dir="rtl">
      <Sidebar
        isOpen={isMobileSidebarOpen}
        isCollapsed={isSidebarCollapsed}
        onClose={() => setIsMobileSidebarOpen(false)}
        onToggleCollapse={() => setIsSidebarCollapsed(value => !value)}
        onRequestLogout={() => setIsLogoutOpen(true)}
        userAuth={auth}
      />

      <div
        className={cn(
          'min-h-screen transition-[padding] duration-200',
          isSidebarCollapsed ? 'lg:pr-24' : 'lg:pr-72'
        )}
      >
        {isPrivateStaging && (
          <div
            className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-xs font-bold text-amber-950"
            role="status"
          >
            محیط پیش‌انتشار خصوصی است؛ داده‌های مالی ممکن است پیش از انتشار بازنشانی شوند.
          </div>
        )}

        <TopBar
          onOpenMobileMenu={() => setIsMobileSidebarOpen(true)}
          onRequestLogout={() => setIsLogoutOpen(true)}
          userAuth={auth}
        />

        <main className="focus:outline-none">
          <div className="mx-auto max-w-[1480px] px-4 py-5 sm:px-6 lg:px-8 lg:py-7">{children}</div>
        </main>
      </div>

      {isLogoutOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="logout-dialog-title"
        >
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
            <div className="flex items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-red-50 text-red-600">
                <LogoutIcon className="h-5 w-5" />
              </span>
              <div>
                <h2 id="logout-dialog-title" className="text-lg font-black text-slate-950">
                  خروج از حساب کاربری
                </h2>
                <p className="mt-2 text-sm font-medium leading-7 text-slate-500">
                  آیا مطمئن هستید که می‌خواهید از پنل مدیریت خارج شوید؟
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                className="pm-button-secondary h-11 px-5"
                onClick={() => setIsLogoutOpen(false)}
              >
                انصراف
              </button>
              <button type="button" className="pm-button-danger h-11 px-5" onClick={confirmLogout}>
                تأیید خروج
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
